import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Loader2, Lock, ShieldCheck, KeyRound, Download, ArrowLeft,
  FileText, FileArchive, FileCode, FileJson, FileSpreadsheet, FileKey,
  Package,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { verifyExpediente, getExpedienteManifiesto, expedienteDownloadUrl } from "@/lib/api";
import { toast } from "sonner";
import logo from "@/assets/sortiven-logo.png";

// Los 9 requerimientos oficiales de SENCAMER (en el orden solicitado)
const REQUISITOS = [
  { num: 0, titulo: "Índice del expediente" },
  { num: 1, titulo: "Manual de usuario e instalación" },
  { num: 2, titulo: "Documentación básica de la aplicación y descripción de tipos de sorteos" },
  { num: 3, titulo: "Todos los archivos de la aplicación (código fuente, ejecutables, bibliotecas)" },
  { num: 4, titulo: "Lista de requerimientos de software para compilación y ejecución (con versiones)" },
  { num: 5, titulo: "Compilador o IDE privativo (si aplica)" },
  { num: 6, titulo: "Usuarios y contraseñas para pruebas por perfil" },
  { num: 7, titulo: "Firmas digitales (MD5) de todos los archivos" },
  { num: 8, titulo: "Descripción detallada de sorteos con rangos" },
  { num: 9, titulo: "Histórico de juegos o sorteos realizados" },
];

// Expedientes disponibles (uno por juego homologado)
const EXPEDIENTES = [
  {
    key: "triple",
    nombre: "TripleFalcón Venezuela",
    descripcion: "Expediente del sorteo TripleFalcón Venezuela",
  },
  {
    key: "zoolo",
    nombre: "ZooloFalcón Venezuela",
    descripcion: "Expediente del sorteo ZooloFalcón Venezuela",
  },
];

function iconFor(filename) {
  const ext = filename.split(".").pop().toLowerCase();
  const map = {
    zip: FileArchive, md: FileText, txt: FileKey,
    csv: FileSpreadsheet, json: FileJson, py: FileCode,
  };
  return map[ext] || FileText;
}

function humanSize(bytes) {
  if (!bytes) return "—";
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

function ExpedienteContent({ password, manifiesto, juego, cargando, onCambiarJuego }) {
  // Agrupar items por número de requerimiento
  const porReq = {};
  for (const it of manifiesto.items) {
    if (!porReq[it.req]) porReq[it.req] = [];
    porReq[it.req].push(it);
  }

  const handleDownload = (itemId, filename) => {
    const url = expedienteDownloadUrl(itemId, password, juego);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const downloadAll = () => {
    const zipItem = manifiesto.items.find((x) => x.id === "zip-fuente");
    if (zipItem) handleDownload(zipItem.id, zipItem.filename);
    else toast.error("Paquete completo no disponible");
  };

  const etiqueta = manifiesto.etiqueta || "—";

  return (
    <div className="mx-auto max-w-4xl space-y-8 px-4 py-10">
      {/* Encabezado */}
      <header className="flex flex-col items-center gap-4 text-center">
        <img src={logo} alt="SORTIVEN" className="h-14 w-auto" />
        <div>
          <h1 className="font-display text-3xl font-bold text-foreground sm:text-4xl">
            Expediente Regulatorio
          </h1>
          <p className="mt-2 text-base text-muted-foreground">
            Documentación técnica ordenada según los <strong className="text-foreground">nueve
            requerimientos formales</strong> solicitados por el ente regulador.
          </p>
        </div>

        {/* Selector de juego */}
        <div
          className="w-full rounded-xl border border-border bg-card p-4 shadow-sm"
          data-testid="expediente-selector-juego"
        >
          <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Juego homologado
          </p>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {EXPEDIENTES.map((exp) => {
              const activo = exp.key === juego;
              return (
                <button
                  key={exp.key}
                  type="button"
                  onClick={() => onCambiarJuego(exp.key)}
                  data-testid={`expediente-juego-${exp.key}`}
                  className={`rounded-lg border p-3 text-left transition-colors ${
                    activo
                      ? "border-primary bg-primary/5 ring-2 ring-primary/30"
                      : "border-border hover:bg-muted/50"
                  }`}
                >
                  <p className="font-semibold text-foreground">
                    Expediente: {exp.nombre}
                  </p>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    {exp.descripcion}
                  </p>
                </button>
              );
            })}
          </div>
          <p className="mt-3 flex items-center justify-center gap-2 text-xs text-muted-foreground" data-testid="expediente-visualizando">
            Visualizando: <strong className="text-foreground">{etiqueta}</strong>
            {cargando && <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" data-testid="expediente-cargando" />}
          </p>
        </div>

        <Button
          size="lg"
          className="gap-2"
          onClick={downloadAll}
          data-testid="expediente-download-all-btn"
        >
          <Package className="h-4 w-4" /> Descargar código fuente (ZIP)
        </Button>
      </header>

      {/* Cada requerimiento como una tarjeta */}
      <div className="space-y-6">
        {REQUISITOS.map((req) => {
          const items = porReq[req.num] || [];
          const isNoAplica = req.num === 5 && items.length > 0;
          return (
            <Card key={req.num} data-testid={`expediente-req-${req.num}`}>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-start gap-3 text-base sm:text-lg">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 font-semibold text-primary">
                    {req.num}
                  </span>
                  <span className="pt-0.5">{req.titulo}</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                {items.length === 0 ? (
                  <p className="text-sm italic text-muted-foreground">
                    No se entregan archivos para este requerimiento.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {isNoAplica && (
                      <div className="mb-3 rounded-md border border-emerald-500/30 bg-emerald-500/5 p-3 text-xs text-emerald-700 dark:text-emerald-400">
                        <strong>No aplica:</strong> el sistema utiliza exclusivamente
                        software de licencia libre. Se entrega la declaración formal.
                      </div>
                    )}
                    {items.map((it) => {
                      const Icon = iconFor(it.filename);
                      return (
                        <div
                          key={it.id}
                          className="flex flex-col gap-3 rounded-lg border border-border p-3 sm:flex-row sm:items-center sm:justify-between"
                          data-testid={`expediente-item-${it.id}`}
                        >
                          <div className="flex min-w-0 items-start gap-3">
                            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
                              <Icon className="h-4 w-4" />
                            </span>
                            <div className="min-w-0 flex-1">
                              <p className="truncate font-medium text-foreground">
                                {it.titulo}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {it.descripcion}
                              </p>
                              <p className="mt-1 flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
                                <code className="max-w-full break-all rounded bg-muted px-1.5 py-0.5 font-mono">
                                  {it.filename}
                                </code>
                                <span>·</span>
                                <span>{humanSize(it.size_bytes)}</span>
                              </p>
                            </div>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            className="shrink-0 gap-2"
                            disabled={!it.disponible}
                            onClick={() => handleDownload(it.id, it.filename)}
                            data-testid={`expediente-download-${it.id}`}
                          >
                            <Download className="h-4 w-4" />
                            {it.disponible ? "Descargar" : "No disponible"}
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      <footer className="border-t border-border pt-6 text-center text-xs text-muted-foreground">
        Documentación de uso interno del departamento legal. Los archivos son
        entregables oficiales para el ente regulador.
      </footer>
    </div>
  );
}

export default function Expediente() {
  const [unlocked, setUnlocked] = useState(false);
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [manifiesto, setManifiesto] = useState(null);
  const [sessionPwd, setSessionPwd] = useState("");
  const [juego, setJuego] = useState("triple");
  const [cargando, setCargando] = useState(false);

  useEffect(() => {
    if (!unlocked || !sessionPwd) return;
    let ignore = false;
    setCargando(true);
    getExpedienteManifiesto(sessionPwd, juego)
      .then((m) => { if (!ignore) setManifiesto(m); })
      .catch(() => {
        if (ignore) return;
        toast.error("No se pudo cargar el expediente. Intente de nuevo.");
        setUnlocked(false);
        setSessionPwd("");
        setManifiesto(null);
      })
      .finally(() => { if (!ignore) setCargando(false); });
    return () => { ignore = true; };
  }, [unlocked, sessionPwd, juego]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await verifyExpediente(password);
      setSessionPwd(password);
      setUnlocked(true);
      setPassword("");
    } catch {
      toast.error("Contraseña incorrecta");
    } finally {
      setLoading(false);
    }
  };

  if (unlocked && manifiesto) {
    return (
      <div data-theme="institutional" className="min-h-screen bg-background" data-testid="expediente-content">
        <div className="border-b border-border bg-card/50">
          <div className="mx-auto flex max-w-4xl items-center justify-between px-4 py-3">
            <Link to="/" className="flex items-center gap-2 text-sm text-primary hover:underline" data-testid="expediente-home-link">
              <ArrowLeft className="h-4 w-4" /> Volver al sitio
            </Link>
            <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <ShieldCheck className="h-3.5 w-3.5 text-green-600" /> Acceso autorizado
            </span>
          </div>
        </div>
        <ExpedienteContent
          password={sessionPwd}
          manifiesto={manifiesto}
          juego={juego}
          cargando={cargando}
          onCambiarJuego={setJuego}
        />
      </div>
    );
  }

  if (unlocked && !manifiesto) {
    return (
      <div data-theme="institutional" className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div data-theme="institutional" className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-md">
        <Link to="/" className="mb-6 flex items-center justify-center">
          <img src={logo} alt="SORTIVEN" className="h-14 w-auto" />
        </Link>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 font-display text-xl">
              <Lock className="h-5 w-5 text-primary" /> Expediente — Acceso restringido
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="mb-4 text-sm text-muted-foreground">
              Documentación regulatoria de uso interno. Introduzca la contraseña
              proporcionada por el departamento legal para continuar.
            </p>
            <form onSubmit={onSubmit} className="space-y-4">
              <div>
                <Label htmlFor="expediente-password">Contraseña</Label>
                <Input
                  id="expediente-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoFocus
                  data-testid="expediente-password-input"
                />
              </div>
              <Button
                type="submit"
                className="w-full gap-2"
                disabled={loading}
                data-testid="expediente-submit-button"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}
                Acceder
              </Button>
            </form>
            <Link to="/" className="mt-4 block text-center text-xs text-primary hover:underline">
              ← Volver al sitio público
            </Link>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
